import { cn } from "@/lib/cn";
import { statusMeta, statusLabel } from "@/lib/status";

// ============================================================================
// StatusBadge — the ONE way Pegasus renders dispatch/tech status.
//
// One source of truth (lib/status.ts) → three presentational forms:
//
//   variant="icon"  Solid circle + white glyph. Space-efficient, glanceable.
//                   Used on dense Board cards (top-right corner). Renders
//                   NOTHING for "pending" so not-yet-started stops stay clean
//                   (preserves the Board's original look).
//
//   variant="pill"  Tinted background + color dot + label. The default for
//                   detail pages and tables — readable, calm, AA-contrast.
//
//   variant="dot"   Color dot + label, no background. The most compact
//                   label form, for very tight rows.
//
// The label is always the RAW status text (see lib/status.ts) — we unify the
// look, never the words.
// ============================================================================

type Variant = "icon" | "pill" | "dot";

interface Props {
  status: string | null | undefined;
  variant?: Variant;
  className?: string;
  /** icon form only — pixel box of the circle. Defaults to the Board size. */
  iconSize?: "sm" | "md";
}

export function StatusBadge({
  status,
  variant = "pill",
  className,
  iconSize = "sm",
}: Props) {
  const meta = statusMeta(status);
  const label = statusLabel(status);

  if (variant === "icon") {
    // Not-yet-started stops show no icon on the Board (original behavior).
    if (meta.key === "pending") return null;
    const box = iconSize === "md" ? "h-6 w-6" : "h-5 w-5";
    const glyph = iconSize === "md" ? "h-3.5 w-3.5" : "h-3 w-3";
    return (
      <span
        className={cn(
          "inline-flex flex-shrink-0 items-center justify-center rounded-full shadow-sm",
          box,
          meta.solidBg,
          className,
        )}
        title={label}
        aria-label={label}
      >
        <meta.Icon className={cn("text-white", glyph)} strokeWidth={2.5} aria-hidden="true" />
      </span>
    );
  }

  if (variant === "dot") {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1.5 text-xs font-medium",
          meta.pillText,
          className,
        )}
      >
        <span className={cn("h-1.5 w-1.5 flex-shrink-0 rounded-full", meta.dot)} aria-hidden="true" />
        {label}
      </span>
    );
  }

  // pill
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium",
        meta.pillBg,
        meta.pillText,
        className,
      )}
    >
      <span className={cn("h-1.5 w-1.5 flex-shrink-0 rounded-full", meta.dot)} aria-hidden="true" />
      {label}
    </span>
  );
}
